<style scoped>
.video-container {
    position: relative;
    @apply flex flex-row;
}

.video-container > video {
    @apply w-full mx-auto;
}

.play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    max-height: 100%;
    max-width: 100%;
    -webkit-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
}
.play-button:focus {
    outline: 0;
}

.button-panel {
    position: absolute;
    bottom: 0%;
    left: 0%;
    width: 100%;
    opacity: 0.5;
    @apply flex flex-col;
}

.skip-button {
    position: absolute;
    top: 0%;
    right: 0%;
    min-width: 8rem;
    opacity: 0.5;
    @apply p-2 ml-auto mb-4 text-white border border-white bg-black;
}
.skip-button:focus {
    @apply outline-none;
}

.controls {
    @apply flex bg-black text-white;
}
</style>

<template>
    <div
        class="video-container"
        ref="videoContainer"
        @mouseover="isMouseover = true"
        @mouseleave="isMouseover = false"
    >
        <video
            ref="video"
            :muted="muted"
            :loop="!endImageUrl"
            playsinline
            preload="auto"
            :poster="poster"
            @click="videoClick"
            @seeked="seeked"
            @ended="ended"
        >
            <!-- @waiting="startCountdown" -->
            <source ref="videoSource" :type="videoType" />
        </video>
        <button
            v-if="showSkipButton"
            class="skip-button"
            @click="
                () => {
                    if (countdownSeconds === 0) {
                        $emit('skip');
                    }
                }
            "
        >
            {{
                countdownSeconds > 0 ? `${countdownSeconds} 秒後可跳過` : 'Skip'
            }}
        </button>
        <div class="button-panel">
            <div v-if="muted" class="mr-auto px-2 bg-black text-white">
                {{ '請按🔊開啟音效' }}
            </div>
            <div
                v-if="isMouseover || !playing || isFullScreen"
                class="controls"
            >
                <button
                    class=" focus:outline-none"
                    @click="
                        () => {
                            if (!playing || allowPause) {
                                togglePlay();
                            }
                        }
                    "
                >
                    <div class="icon w-6 h-6 ">
                        <pause v-if="!!playing" />
                        <playArrow v-else />
                    </div>
                </button>

                <button class=" focus:outline-none" @click="muted = !muted">
                    <div class="icon w-6 h-6">
                        <volumeOff v-if="!!muted" />
                        <volumeUp v-else />
                    </div>
                </button>

                <button
                    class="ml-auto focus:outline-none"
                    @click="toggleFullScreen"
                >
                    <div class="icon w-6 h-6">
                        <svg viewBox="0 0 24 24">
                            <path
                                v-if="!isFullScreen"
                                d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
                            />
                            <path
                                v-else
                                d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
                            />
                        </svg>
                    </div>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import playArrow from '../assets/svg/play_arrow.svg';
import pause from '../assets/svg/pause.svg';
import volumeUp from '../assets/svg/volume_up.svg';
import volumeOff from '../assets/svg/volume_off.svg';

export default {
    components: { playArrow, pause, volumeUp, volumeOff },
    props: {
        videoSrc: { type: String, required: true },
        videoType: { type: String, default: 'video/mp4' },
        coverImageUrl: { type: String },
        endImageUrl: { type: String },
        autoPlay: { type: Boolean, default: false },
        allowPause: { type: Boolean, default: false },
        canSkipAfterSeconds: { type: Number }
    },
    data() {
        return {
            src: undefined,
            poster: undefined,
            playing: false,
            isMouseover: false,
            duration: undefined,
            showSkipButton: false,
            countdownSeconds: undefined,
            supportFullScreen: false,
            isFullScreen: false,
            muted: true
        };
    },
    mounted() {
        this.$watch(
            'videoSrc',
            newVal => {
                this.videoSource.src = newVal;
            },
            { immediate: true }
        );
        this.poster = this.coverImageUrl;
        if (this.autoPlay) {
            // Do not emit error event, since error event may be caused by browser auto-play policy
            if (this.poster) {
                setTimeout(() => {
                    this.play(false);
                }, 2 * 1000);
            } else {
                this.play(false);
            }
        }
    },
    computed: {
        video() {
            return this.$refs.video;
        },
        videoSource() {
            return this.$refs.videoSource;
        }
    },
    beforeDestroy() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
    },
    methods: {
        videoClick() {
            this.$emit('videoClick', { playing: this.playing });
        },
        togglePlay() {
            if (!this.playing) {
                this.play();
            } else if (this.allowPause) {
                this.pause();
            }
        },
        async play(emitErrorEvent = true) {
            try {
                await this.video.play();
                this.playing = true;
                this.duration = Math.ceil(this.video.duration);
                this.startCountdown();
            } catch (err) {
                /* eslint-disable no-console */
                console.error('error play video', err);
                if (emitErrorEvent) this.$emit('error');
            }
        },
        pause() {
            this.video.pause();
            this.playing = false;
        },
        startCountdown() {
            if (!this.canSkipAfterSeconds || this.countdownInterval) {
                return;
            }

            this.countdownSeconds = Math.ceil(this.canSkipAfterSeconds);
            this.showSkipButton = true;
            this.countdownInterval = setInterval(() => {
                this.countdownSeconds--;
                if (this.countdownSeconds <= 0) {
                    clearInterval(this.countdownInterval);
                    this.$emit('canSkip');
                }
                this.$emit('countdownSeconds', this.countdownSeconds);
            }, 1 * 1000);
        },
        checkSupportFullScreen() {
            const el = this.$refs.videoContainer;
            if (
                el.requestFullscreen ||
                el.mozRequestFullScreen ||
                el.webkitRequestFullScreen ||
                el.msRequestFullscreen
            ) {
                this.supportFullScreen = true;
            } else {
                this.supportFullScreen = false;
            }
        },
        toggleFullScreen() {
            const doc = window.document;
            const el = this.$refs.videoContainer;
            const requestFullScreen =
                el.requestFullscreen ||
                el.mozRequestFullScreen ||
                el.webkitRequestFullScreen ||
                el.msRequestFullscreen;
            const cancelFullScreen =
                doc.exitFullscreen ||
                doc.mozCancelFullScreen ||
                doc.webkitExitFullscreen ||
                doc.msExitFullscreen;
            if (
                !doc.fullscreenElement &&
                !doc.mozFullScreenElement &&
                !doc.webkitFullscreenElement &&
                !doc.msFullscreenElement
            ) {
                requestFullScreen.call(el);
                this.isFullScreen = true;
            } else {
                cancelFullScreen.call(doc);
                this.isFullScreen = false;
            }
        },
        seeked() {
            // since video is looping, ended event will not be fired
            // use seeked event to emit 'ended'
            // https://stackoverflow.com/questions/8153703/html5-video-ended-event-not-working-in-chrome-and-ie
            this.$emit('ended');
        },
        ended() {
            if (this.endImageUrl) {
                this.poster = this.endImageUrl;
                this.video.load();
            }
            this.playing = false;
            this.$emit('ended');
        }
    }
};
</script>
